import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Icon } from '@reservamos/elements';
import { v4 as uuid } from 'uuid';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import hasAppliedDiscount from 'utils/purchase/hasAppliedDiscount';
import { useReserhubAccount } from '@/context/ReserhubAccount';
import usePurchase from 'hooks/store/usePurchase';
import useExperimentalFeature from 'hooks/useExperimentalFeature';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { getPurchase } from '@/actions/purchase';
import { setError } from '@/actions';
import setCdsPointsPreview from '@/actions/cdsPoints';
import { purchaseApi } from '@reservamos/js-api-client/purchase';
import { readClientApiKey } from 'utils/clientApiKey';
import { trackCdsPoints } from '@/metrics/user-analytics/cdsPoints';
import WalletCard from '@/components/Wallet/WalletCard';
import WalletSummary from '@/components/Wallet/WalletSummary';
import { getCommerceApiClient } from '@/services/commerce-api/commerceApiClient';
import {
  readCdsPointsRecovery,
  hasCdsAccount,
  persistCdsPointsAttempt,
  clearCdsPointsAttempt,
  cdsPointsAttemptKey,
} from 'utils/purchase/cdsPointsRecovery';

import cdsWordmark from 'images/brands-unique/cds/wallet-wordmark.webp';
import tierIcons from './cdsTierIcons';
import CdsWalletSkeleton from './CdsWalletSkeleton';
import './CdsPointsCheckout.scss';

const rejectionMessage = 'El canje fue rechazado y no se utilizaron puntos.';
const pendingStatuses = ['prepared', 'submitting'];
const isIdentifiedUnknown = (operation) => Boolean(operation?.id) && operation.status === 'unknown';
const { cdsPoints } = purchaseApi.loyalty;
// Compare decimal strings without rounding provider point amounts through floating point.
const decimalAmount = (value) =>
  String(value)
    .replace(/(\.\d*?)0+$/, '$1')
    .replace(/\.$/, '');
/** Restore the server's rejected operation after checkout loses its active redemption ID.
 * @param {object} purchase - Persisted purchase state.
 * @returns {object|null} Rejected operation with normalized point consumption.
 */
const readPersistedRejection = (purchase) => {
  const operation = purchase.cdsPointsRedemption;
  if (purchase.usesCdsPoints !== true || operation?.status !== 'rejected') return null;
  return { ...operation, points_used: operation.points_used ?? operation.pointsUsed };
};
const beforeRedemptionErrors = [
  'quote_expired',
  'cart_changed',
  'forbidden',
  'unauthorized',
  'points_disabled',
  'purchase_unavailable',
  'invalid_provider',
  'cart_not_locked',
  'points_unavailable',
  'points_insufficient',
  'points_coupon_conflict',
];

/** Simple full-cart CDS wallet with durable operation recovery. */
export default function CdsPointsCheckout({
  onBlockingChange,
  prepareFinalization,
  onCheckoutChange,
  onProcessingChange,
  registerFinalize,
}) {
  const { user, isUserLoading, isUserFetched } = useReserhubAccount();
  const purchase = usePurchase();
  const { t } = useTranslation('payment');
  const [couponRejected, setCouponRejected] = useState(false);
  const travelerPromoApplied = Boolean(purchase.promoTravelerCode?.trim());
  const couponConflict =
    travelerPromoApplied ||
    purchase.cdsPointsCouponConflict === true ||
    hasAppliedDiscount(purchase) ||
    couponRejected;
  const couponUnavailable =
    couponConflict || Boolean(purchase.applyingDiscount || purchase.promoCodeSubmitting);
  const couponMessage = t(
    travelerPromoApplied ? 'cds_points_traveler_promo_conflict' : 'cds_points_coupon_conflict',
  );
  const persistedRejection = readPersistedRejection(purchase);
  const walletOffered = useExperimentalFeature('CDS_POINTS_REDEMPTION_ENABLED') ?? true;
  const env = useWhitelabelEnvs();
  const dispatch = useDispatch();
  const cdsAccountEnabled = hasCdsAccount(env);
  const [quote, setQuote] = useState(null);
  const [selected, setSelected] = useState(false);
  const [selectionReady, setSelectionReady] = useState(false);
  const [initialRecovery] = useState(() => {
    return readCdsPointsRecovery(purchase, user?.id, cdsAccountEnabled);
  });
  const [recoveryOwner, setRecoveryOwner] = useState(initialRecovery.ownerId);
  const [operation, setOperation] = useState(() => {
    if (persistedRejection) return persistedRejection;
    if (purchase.cdsPointsRedemptionId)
      return { id: purchase.cdsPointsRedemptionId, status: 'submitting' };
    return initialRecovery.blocked ? { status: 'unknown' } : null;
  });
  const [loading, setLoading] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [pollRetrying, setPollRetrying] = useState(false);
  const [profile, setProfile] = useState(null);
  const [message, setMessage] = useState('');
  const [retry, setRetry] = useState(0);
  const busy = useRef(false);
  const generation = useRef(0);
  const notifiedRejections = useRef(new Set());
  const dismissedRejection = useRef(null);
  const attempt = useRef(initialRecovery.attempt);
  const authenticated = Boolean(user?.id && user?.sessionToken);
  const canRedeem = Boolean(purchase.cdsPointsEnabled && authenticated);
  const visible = Boolean(
    (canRedeem && walletOffered) ||
      selected ||
      purchase.usesCdsPoints ||
      Number(purchase.cdsPointsUsed) > 0 ||
      purchase.cdsPointsRedemptionId ||
      operation,
  );
  const canLoadWallet = canRedeem && visible && !couponUnavailable;
  const scope = useRef(purchase.token);
  const storageKey = cdsPointsAttemptKey(purchase.token, user?.id);
  const accountMismatch = Boolean(recoveryOwner && recoveryOwner !== String(user?.id));
  const context = {
    baseUrl: env.api?.purchaseUrl,
    token: purchase.token,
    sessionToken: user?.sessionToken,
    apiKey: readClientApiKey(),
  };
  const blocked = selected || Boolean(operation && operation.status !== 'rejected');
  const consumptionConfirmed = Number(operation?.points_used) > 0;

  /** Release only a pre-dispatch choice and refresh the authoritative coupon state. */
  const handleCouponConflict = () => {
    setSelected(false);
    setSelectionReady(false);
    setCouponRejected(true);
    dispatch(getPurchase(purchase.token));
  };

  useEffect(() => {
    if (
      !operation?.id ||
      operation.status !== 'rejected' ||
      operation.points_used == null ||
      decimalAmount(operation.points_used) !== '0'
    )
      return;
    const key = `${purchase.token}:${operation.id}`;
    if (notifiedRejections.current.has(key)) return;
    notifiedRejections.current.add(key);
    dispatch(
      setError(null, 'cds_points_rejected', 'error', false, rejectionMessage, null, null, 'fixed'),
    );
  }, [dispatch, purchase.token, operation?.id, operation?.status, operation?.points_used]);

  const canLoadCategory = canRedeem && visible && !operation;
  const profileScope = `${user?.id}:${user?.sessionToken}`;
  const currentProfile = profile?.scope === profileScope ? profile : null;
  const categoryValue =
    typeof currentProfile?.category === 'string'
      ? currentProfile.category.trim().toLowerCase()
      : '';
  const category = categoryValue
    ? categoryValue.charAt(0).toUpperCase() + categoryValue.slice(1)
    : null;

  useEffect(() => {
    if (!canLoadCategory) return undefined;
    let stopped = false;
    setProfile({ scope: profileScope, loading: true });
    const profileOptions = { includeBalance: couponConflict };
    Promise.resolve()
      .then(() => getCommerceApiClient().accounts.getCdsPassengerProfile(profileOptions))
      .then((result) => {
        if (!stopped)
          setProfile({
            scope: profileScope,
            category: result.category,
            balance: result.wallet?.balance,
            loading: false,
          });
      })
      .catch(() => {
        if (!stopped) setProfile({ scope: profileScope, loading: false });
      });
    return () => {
      stopped = true;
    };
  }, [canLoadCategory, profileScope, couponConflict]);

  useEffect(() => {
    setCouponRejected(false);
  }, [storageKey, purchase.cdsPointsCouponConflict, purchase.discount?.id]);

  useLayoutEffect(() => {
    if (couponUnavailable && !operation && !attempt.current) {
      setSelected(false);
      setQuote(null);
      setSelectionReady(false);
    }
  }, [couponUnavailable, operation]);

  // A promo invalidates pre-dispatch quotes, while an existing operation keeps its response owner.
  const promoChoiceBlocked =
    !operation && !attempt.current && Boolean(purchase.promoCodeSubmitting || travelerPromoApplied);
  const economicScope = JSON.stringify([
    promoChoiceBlocked,
    Boolean(
      purchase.cdsPointsCouponConflict &&
        (!travelerPromoApplied || (!operation && !attempt.current)),
    ),
    purchase.discount?.id,
    purchase.total,
    purchase.extras,
    purchase.insuranceAmount,
    purchase.carbonOffsetAmount,
    purchase.discountCode,
    purchase.serviceLevelAmount,
    purchase.passengers,
    [purchase.departs, purchase.returns].map((way) => [
      way?.id,
      way?.lockId,
      way?.tickets,
      way?.fragments?.map((fragment) => [
        fragment.id,
        fragment.trip?.id,
        fragment.total,
        fragment.tickets,
      ]),
    ]),
  ]);

  useLayoutEffect(() => {
    onBlockingChange(blocked);
  }, [blocked, onBlockingChange]);

  useEffect(() => {
    generation.current += 1;
    setQuote(null);
    setSelectionReady(false);
    setMessage('');
    setLoading(false);
    setFinalizing(false);
    setPollRetrying(false);
    setSelected(false);
    busy.current = false;
    if (scope.current !== purchase.token) {
      scope.current = purchase.token;
      attempt.current = null;
      setOperation(null);
    }
    const recovery = readCdsPointsRecovery(purchase, user?.id, cdsAccountEnabled);
    setRecoveryOwner(recovery.ownerId);
    attempt.current = authenticated ? recovery.attempt : null;
    const rejectionDismissed =
      persistedRejection &&
      dismissedRejection.current === `${purchase.token}:${persistedRejection.id}`;
    const redemptionId =
      rejectionDismissed && purchase.cdsPointsRedemptionId === persistedRejection.id
        ? null
        : purchase.cdsPointsRedemptionId;
    if (persistedRejection && !rejectionDismissed) {
      setOperation(persistedRejection);
      clearCdsPointsAttempt(purchase.token, user?.id);
      attempt.current = null;
      return () => {
        generation.current += 1;
      };
    }
    if (recovery.blocked && !redemptionId) {
      setOperation({ status: 'unknown' });
      setMessage(
        recovery.unavailable
          ? 'No pudimos leer el intento guardado. Habilita el almacenamiento o contacta a soporte.'
          : 'Vuelve a consultar el canje para recuperar la respuesta.',
      );
    }
    // Losing authentication invalidates responses, but never releases an existing settlement.
    if (!authenticated) {
      if (redemptionId) {
        setOperation((previous) => {
          if (previous?.id === redemptionId) return previous;
          return { id: redemptionId, status: 'submitting' };
        });
      }
      return () => {
        generation.current += 1;
      };
    }
    const id = redemptionId || attempt.current?.id;
    if (id) {
      setOperation((previous) => (previous?.id === id ? previous : { id, status: 'submitting' }));
    } else if (attempt.current) {
      setOperation({ status: 'unknown' });
      setMessage('Vuelve a consultar el canje para recuperar la respuesta.');
    }
    return () => {
      generation.current += 1;
    };
    // A session/cart change invalidates responses; ordinary purchase refreshes do not reset the quote.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storageKey,
    authenticated,
    purchase.cdsPointsRedemptionId,
    persistedRejection?.id,
    user?.sessionToken,
    cdsAccountEnabled,
    economicScope,
  ]);

  useEffect(() => {
    if (
      !authenticated ||
      !operation?.id ||
      operation.status === 'rejected' ||
      operation.status === 'confirmed'
    )
      return undefined;
    const { current } = generation;
    let timer;
    let stopped = false;
    let reads = 0;
    const scheduleRead = (nextRead) => {
      if (reads >= 10) {
        setPollRetrying(false);
        setMessage('No pudimos consultar el estado. Vuelve a cargar la compra.');
        return;
      }
      timer = setTimeout(nextRead, Math.min(2000 * 2 ** (reads - 1), 30000));
    };
    /** Poll the identified operation without replaying its mutation. */
    const read = async () => {
      reads += 1;
      try {
        const result = await cdsPoints.getRedemption(context, operation.id);
        if (stopped || current !== generation.current) return;
        setOperation(result);
        setPollRetrying(false);
        setMessage('');
        if (result.status === 'rejected') {
          setSelected(false);
        }
        if (result.status === 'rejected' || result.purchase_state === 'completed') {
          clearCdsPointsAttempt(purchase.token, user?.id);
          attempt.current = null;
        }
        const debitConfirmed = Number(result.points_used) > 0;
        if (debitConfirmed || ['completed', 'canceled'].includes(result.purchase_state)) {
          // The existing payment route owns purchase follow-up once consumption is certain.
          dispatch(getPurchase(purchase.token));
        } else if (pendingStatuses.includes(result.status) || result.status === 'unknown') {
          scheduleRead(read);
        }
      } catch (error) {
        if (!stopped && current === generation.current) {
          const transient = error.status
            ? error.status >= 500 || error.status === 429
            : !error.code || error.code === 'points_unavailable';
          if (!transient) {
            setPollRetrying(false);
            setMessage('No pudimos consultar el estado. Vuelve a cargar la compra.');
            return;
          }
          setPollRetrying(true);
          setMessage('No pudimos consultar el estado. Estamos intentando nuevamente.');
          scheduleRead(read);
        }
      }
    };
    read();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
    // The operation and session own this poll, not other Purchase refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    operation?.id,
    storageKey,
    user?.sessionToken,
    purchase.cdsPointsRedemptionId,
    economicScope,
  ]);

  useEffect(() => {
    if (!canLoadWallet) {
      if (!operation && !attempt.current) {
        busy.current = false;
        setLoading(false);
      }
      return undefined;
    }
    if (operation || attempt.current) return undefined;
    const { current } = generation;
    let stopped = false;
    busy.current = true;
    setLoading(true);
    setMessage('');
    setSelectionReady(false);
    const hydrate = async () => {
      try {
        const result = await cdsPoints.quote(context);
        if (stopped || current !== generation.current) return;
        setQuote(result);
        setSelectionReady(true);
      } catch (error) {
        if (stopped || current !== generation.current) return;
        if (error.code === 'points_coupon_conflict') {
          handleCouponConflict();
        } else {
          setMessage(
            error.code === 'points_insufficient'
              ? 'No tienes puntos suficientes para esta compra.'
              : 'No pudimos consultar tus puntos. Inténtalo nuevamente.',
          );
        }
      } finally {
        if (!stopped && current === generation.current) {
          busy.current = false;
          setLoading(false);
        }
      }
    };
    hydrate();
    return () => {
      stopped = true;
    };
    // Only a session or economic-cart change owns automatic quotation, never render/object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, user?.sessionToken, canLoadWallet, economicScope, retry]);

  /** Submit or recover the durable quote attempt. */
  const confirm = async () => {
    if (
      busy.current ||
      !authenticated ||
      (!attempt.current && (!canRedeem || !selected || couponUnavailable))
    )
      return;
    busy.current = true;
    setLoading(true);
    setMessage('');
    const { current } = generation;
    const recovering = Boolean(attempt.current);
    let replayingAbsent = false;
    if (!recovering) {
      try {
        setFinalizing(true);
        if (prepareFinalization && (await prepareFinalization()) === false) return;
        if (current !== generation.current) return;
        const fresh = await cdsPoints.quote(context);
        if (current !== generation.current) return;
        if (
          Date.parse(quote.expires_at) <= Date.now() ||
          decimalAmount(fresh.points_amount) !== decimalAmount(quote.points_amount)
        ) {
          setSelected(false);
          setQuote(fresh);
          setMessage('La cotización cambió o venció. Confirma nuevamente con Usar puntos.');
          return;
        }
        attempt.current = { quoteId: quote.id, key: uuid() };
      } catch (error) {
        if (current === generation.current) {
          setSelectionReady(false);
          if (error.code === 'points_coupon_conflict') {
            handleCouponConflict();
          } else {
            setMessage(
              error.code === 'points_insufficient'
                ? 'No tienes puntos suficientes para esta compra.'
                : 'No pudimos validar tus puntos. Inténtalo nuevamente.',
            );
          }
        }
        return;
      } finally {
        if (!attempt.current && current === generation.current) {
          busy.current = false;
          setLoading(false);
          setFinalizing(false);
        }
      }
    }
    // Persist before sending: a lost response must recover this same operation.
    try {
      persistCdsPointsAttempt(purchase.token, user.id, attempt.current);
    } catch {
      if (!recovering) {
        attempt.current = null;
        // Nothing was sent: discard any first record written before the marker failed.
        try {
          clearCdsPointsAttempt(purchase.token, user.id);
        } catch {
          /* Storage remains unavailable. */
        }
      }
      busy.current = false;
      setLoading(false);
      setFinalizing(false);
      setMessage(
        'No pudimos guardar el intento. Habilita el almacenamiento del navegador antes de canjear.',
      );
      return;
    }
    setOperation({ status: 'submitting' });
    try {
      let result;
      try {
        result = recovering
          ? await cdsPoints.findRedemption(context, attempt.current.key)
          : await cdsPoints.redeem(context, attempt.current.quoteId, attempt.current.key);
      } catch (error) {
        if (!recovering || error.code !== 'not_found' || !canRedeem) throw error;
        if (current !== generation.current) return;
        replayingAbsent = true;
        result = await cdsPoints.redeem(context, attempt.current.quoteId, attempt.current.key);
      }
      if (current !== generation.current) return;
      attempt.current.id = result.id;
      persistCdsPointsAttempt(purchase.token, user.id, attempt.current);
      setOperation(result);
      setSelected(false);
      if (result.status === 'rejected' || result.purchase_state === 'completed') {
        clearCdsPointsAttempt(purchase.token, user?.id);
        attempt.current = null;
        setQuote(null);
      }
      dispatch(getPurchase(purchase.token));
    } catch (error) {
      if (current === generation.current) {
        if (beforeRedemptionErrors.includes(error.code) && (!recovering || replayingAbsent)) {
          clearCdsPointsAttempt(purchase.token, user?.id);
          attempt.current = null;
          setSelected(false);
          setSelectionReady(false);
          setOperation(null);
          if (error.code === 'points_coupon_conflict') {
            handleCouponConflict();
          } else {
            setMessage('Vuelve a validar tu selección de puntos antes de finalizar.');
          }
        } else {
          setOperation({ status: 'unknown' });
          dispatch(getPurchase(purchase.token));
        }
      }
    } finally {
      if (current === generation.current) {
        busy.current = false;
        setLoading(false);
        setFinalizing(false);
      }
    }
  };

  const select = async () => {
    if (busy.current || !canRedeem || couponUnavailable || !quote || !selectionReady) return;
    const { current } = generation;
    if (Date.parse(quote.expires_at) <= Date.now()) {
      busy.current = true;
      setLoading(true);
      try {
        const fresh = await cdsPoints.quote(context);
        if (current !== generation.current) return;
        setQuote(fresh);
        if (decimalAmount(fresh.points_amount) !== decimalAmount(quote.points_amount)) {
          setMessage('La cantidad de puntos cambió. Confirma nuevamente si quieres usarlos.');
          return;
        }
      } catch (error) {
        if (current === generation.current) {
          setSelectionReady(false);
          if (error.code === 'points_coupon_conflict') {
            handleCouponConflict();
          } else setMessage('No pudimos consultar tus puntos. Inténtalo nuevamente.');
        }
        return;
      } finally {
        if (current === generation.current) {
          busy.current = false;
          setLoading(false);
        }
      }
    }
    if (current !== generation.current) return;
    setSelected(true);
    setMessage('');
    trackCdsPoints('CDS Points Selected', purchase.token, quote.points_amount);
  };

  const clearSelection = () => {
    if (busy.current || operation) return;
    setSelected(false);
    setMessage('');
  };

  const previewAmount = selected && !operation ? quote?.points_amount : null;
  useLayoutEffect(() => {
    dispatch(setCdsPointsPreview(purchase.token, previewAmount));
  }, [dispatch, purchase.token, previewAmount]);
  useLayoutEffect(() => {
    return () => dispatch(setCdsPointsPreview(purchase.token, null));
  }, [dispatch, purchase.token]);

  const canFinalize = Boolean(
    selected &&
      selectionReady &&
      quote &&
      canRedeem &&
      !couponUnavailable &&
      !operation &&
      !loading,
  );
  const processing =
    authenticated &&
    !accountMismatch &&
    (!message || pollRetrying) &&
    (finalizing ||
      consumptionConfirmed ||
      isIdentifiedUnknown(operation) ||
      pendingStatuses.includes(operation?.status) ||
      (operation?.status === 'confirmed' && operation.purchase_state === 'completed'));
  useLayoutEffect(() => {
    onProcessingChange(Boolean(processing));
  }, [processing, onProcessingChange]);
  useLayoutEffect(() => {
    onCheckoutChange({
      selected: selected && !operation,
      loading,
      canFinalize,
      visible: visible && !isUserLoading && isUserFetched !== false,
    });
  }, [
    selected,
    operation,
    loading,
    canFinalize,
    visible,
    isUserLoading,
    isUserFetched,
    onCheckoutChange,
  ]);
  useLayoutEffect(() => {
    registerFinalize(() => {
      if (!canFinalize || busy.current) return;
      trackCdsPoints('CDS Points Confirmation Clicked', purchase.token, quote.points_amount);
      confirm();
    });
    return () => registerFinalize(null);
  });

  const walletQuote =
    quote ||
    (couponUnavailable
      ? {
          balance: currentProfile?.balance ?? '—',
          points_amount: purchase.total ?? '—',
        }
      : null);
  const hasPointsChoice = selected && !couponUnavailable;
  const showSelectedPoints = hasPointsChoice && selectionReady && !loading;
  const loadingWallet = !operation && loading && !finalizing;
  const canRetryQuote =
    canRedeem &&
    !couponUnavailable &&
    !operation &&
    !loading &&
    (!quote || (message && !selectionReady));
  const canClearChoice =
    authenticated && !loading && !operation && selected && (!canRedeem || !quote);

  const renderWalletCard = () => (
    <WalletCard
      currencySymbol="S/"
      amount={showSelectedPoints ? walletQuote.points_amount : walletQuote.balance ?? '—'}
      caption={showSelectedPoints ? 'CASH CDS usando en tu compra' : 'CASH CDS disponible'}
      brand={<img src={cdsWordmark} width="96" height="32" alt="Cruz del Sur" />}
      category={
        category
          ? `Categoría ${category}`
          : currentProfile?.loading && <span role="status">Consultando categoría…</span>
      }
      tierIcon={
        tierIcons[categoryValue] ? (
          <img
            className="cds-wallet-section__tier"
            src={tierIcons[categoryValue]}
            width="30"
            height="30"
            alt=""
          />
        ) : null
      }
      footer={
        showSelectedPoints ? (
          <p>
            <strong>{walletQuote.balance ?? '—'}</strong> saldo actual
          </p>
        ) : (
          <p>Total de esta compra: S/ {walletQuote.points_amount}</p>
        )
      }
      actionLabel={hasPointsChoice ? 'No usar' : 'Usar puntos'}
      onAction={hasPointsChoice ? clearSelection : select}
      disabled={couponUnavailable || loading || (!hasPointsChoice && !selectionReady)}
    />
  );

  return !visible || isUserLoading || isUserFetched === false ? null : (
    <section aria-label="Puntos CDS" className="cds-wallet-section">
      <h3 className="cds-wallet-section__title">Usar puntos</h3>
      {!authenticated && blocked && (
        <p role="status">
          Inicia sesión nuevamente para consultar el canje. Otros pagos siguen bloqueados.
        </p>
      )}
      {loadingWallet && <CdsWalletSkeleton />}
      {authenticated && selected && !canRedeem && !operation && (
        <p role="status">El pago con puntos no está disponible. Puedes elegir otro método.</p>
      )}
      {canRedeem && walletQuote && !operation && !loadingWallet && (
        <div
          className="cds-wallet-section__layout"
          role="group"
          aria-label={couponConflict ? couponMessage : undefined}
          aria-describedby={hasPointsChoice ? undefined : `cds-considerations-${purchase.token}`}
        >
          {renderWalletCard()}
          <div className="cds-wallet-section__details">
            {showSelectedPoints && (
              <WalletSummary
                title="Estás usando tus CASH CDS"
                rows={[
                  { label: 'A usar en esta compra', value: `- S/${walletQuote.points_amount}` },
                  { label: 'Resta por pagar', value: 'S/0.00' },
                ]}
              />
            )}
            {!hasPointsChoice && (
              <aside
                id={`cds-considerations-${purchase.token}`}
                aria-labelledby={`cds-considerations-title-${purchase.token}`}
                className="cds-wallet-section__considerations"
              >
                <span aria-hidden="true">
                  <Icon type="Warning" color="warning" size="S" />
                </span>
                <div>
                  <h4 id={`cds-considerations-title-${purchase.token}`}>
                    {t('cds_points_considerations_title')}
                  </h4>
                  <ul>
                    <li>{t('cds_points_coupon_conflict')}</li>
                    <li>{t('cds_points_single_payment_method')}</li>
                    <li>{t('cds_points_traveler_promo_conflict')}</li>
                  </ul>
                </div>
              </aside>
            )}
          </div>
        </div>
      )}
      {authenticated && accountMismatch && blocked && (
        <p role="status">
          Inicia sesión con la cuenta que inició este canje o contacta a soporte. Otros pagos siguen
          bloqueados.
        </p>
      )}
      {operation && (
        <div role="status" className="mt-3">
          {pendingStatuses.includes(operation.status) &&
            'Confirmando el canje. Espera la respuesta de CDS.'}
          {operation.status === 'unknown' &&
            (consumptionConfirmed
              ? 'CDS confirmó el consumo de puntos. Estamos recuperando tus boletos; contacta a soporte.'
              : 'Estamos consultando el estado de tu compra.')}
          {operation.status === 'rejected' && (
            <>
              <div role="alert">
                <Alert
                  alertType="error"
                  title={rejectionMessage}
                  titleSize="S"
                  iconSize="S"
                  weightTitle="regular"
                />
              </div>
              {canRedeem && (
                <button
                  type="button"
                  className="mt-2 underline"
                  onClick={() => {
                    dismissedRejection.current = `${purchase.token}:${operation.id}`;
                    clearCdsPointsAttempt(purchase.token, user?.id);
                    attempt.current = null;
                    setOperation(null);
                    setRetry((previous) => previous + 1);
                  }}
                >
                  Volver a consultar puntos
                </button>
              )}
            </>
          )}
          {operation.status === 'confirmed' &&
            (operation.purchase_state === 'completed'
              ? 'Compra confirmada. Tus boletos están disponibles.'
              : 'CDS confirmó el consumo de puntos. Estamos recuperando tus boletos; contacta a soporte.')}
          {consumptionConfirmed && <p>Puntos utilizados: {operation.points_used}</p>}
          {authenticated &&
            !accountMismatch &&
            attempt.current &&
            operation.status === 'unknown' &&
            !operation.id && (
              <button
                type="button"
                className="mt-2 block underline"
                disabled={loading}
                onClick={confirm}
              >
                Consultar estado del canje
              </button>
            )}
        </div>
      )}
      {message && !loading && (
        <p role="alert" className="mt-2">
          {message}
        </p>
      )}
      {(canRetryQuote || canClearChoice) && (
        <div className="cds-wallet-section__error-actions">
          {canRetryQuote && (
            <button
              type="button"
              className="underline"
              onClick={() => setRetry((previous) => previous + 1)}
            >
              Volver a consultar puntos
            </button>
          )}
          {canClearChoice && (
            <button type="button" className="underline" onClick={clearSelection}>
              No usar
            </button>
          )}
        </div>
      )}
    </section>
  );
}

CdsPointsCheckout.propTypes = {
  onBlockingChange: PropTypes.func.isRequired,
  prepareFinalization: PropTypes.func,
  onCheckoutChange: PropTypes.func,
  onProcessingChange: PropTypes.func,
  registerFinalize: PropTypes.func,
};
const noop = () => {};
CdsPointsCheckout.defaultProps = {
  prepareFinalization: undefined,
  onCheckoutChange: noop,
  onProcessingChange: noop,
  registerFinalize: noop,
};
